import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * TD-2750 — the hint over a canvas whose plain scroll or one-finger swipe went to the page instead
 * of zooming: the results heatmap (ProfilerHeatmap) and the mini shareholder graph
 * (useMiniGraphGestures) share it, so both read the same. The overlay is the global
 * .heatmap-scroll-hint.
 */

/**
 * The modifier that actually zooms on this machine. Both canvases accept either Ctrl or Cmd, so
 * naming the wrong one in the hint is worse than saying nothing: a Mac user told to press Ctrl
 * will try it, get a page scroll, and conclude the tiles are broken.
 */
export const ZOOM_MODIFIER_LABEL = /Mac|iP(hone|ad|od)/i.test(
  (typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || ''
)
  ? '⌘'
  : 'Ctrl';

/** How long the hint stays after the last gesture that triggered it. */
export const SCROLL_HINT_LINGER_MS = 1200;

export const SCROLL_HINT_TEXT = {
  wheel: `Use ${ZOOM_MODIFIER_LABEL} + scroll to zoom`,
  // Two fingers pinch-zoom. Deliberately not "to move the map" the way embedded maps phrase it:
  // the heatmap has no two-finger pan, so promising movement would be false there.
  touch: 'Use two fingers to zoom'
};

/**
 * The hint on screen, set from the gesture that was just handed to the page, so it always names
 * the gesture the analyst actually made.
 *
 * @returns {[('wheel'|'touch'|null), (kind: 'wheel'|'touch') => void, () => void]} the hint, the
 *   function that puts one up until SCROLL_HINT_LINGER_MS after its last call, and the one that
 *   takes it down at once (for the teardown of the listeners that showed it)
 */
export const useScrollZoomHint = () => {
  const [hint, setHint] = useState(null);
  const timerRef = useRef(null);
  const showHint = useCallback((kind) => {
    // React bails out on an identical value, so a scroll burst re-renders once, not once per
    // wheel event.
    setHint(kind);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHint(null), SCROLL_HINT_LINGER_MS);
  }, []);
  const clearHint = useCallback(() => {
    clearTimeout(timerRef.current);
    setHint(null);
  }, []);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  return [hint, showHint, clearHint];
};
